import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, App, Button, Space, Tag, Tooltip, Typography,
} from 'antd';
import { CopyOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import SkeletonLoading from 'components/SkeletonLoading';
import { connectPosHub, getPosHubIntegrationStatus, syncPosHubMenu } from 'api/merchants';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { StyledWrapper } from './styles';

dayjs.extend(relativeTime);

const STATUS_CONFIG = {
  ACTIVE: { color: 'success', label: 'Active' },
  PENDING: { color: 'warning', label: 'Pending' },
  ERROR: { color: 'error', label: 'Error' },
  NOT_CONNECTED: { color: 'default', label: 'Not Connected' },
};

const MENU_SYNC_POLL_MS = 2000;
const MENU_SYNC_POLL_MAX = 150;

function PosHubIntegrationBox({ restaurantId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const menuSyncPollCount = useRef(0);
  const menuSyncPollTimer = useRef(null);

  const stopMenuSyncPolling = useCallback(() => {
    if (menuSyncPollTimer.current) {
      clearInterval(menuSyncPollTimer.current);
      menuSyncPollTimer.current = null;
    }
    menuSyncPollCount.current = 0;
  }, []);

  useEffect(() => () => stopMenuSyncPolling(), [stopMenuSyncPolling]);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    getPosHubIntegrationStatus(restaurantId)
      .then(setData)
      .catch(captureException)
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    if (searchParams.get('poshub_connected') === 'true') {
      message.success('Successfully connected to POS');
      navigate(`/console/restaurants/${restaurantId}/pos`);
    }
  }, [searchParams, message, navigate, restaurantId]);

  const statusCfg = data
    ? (STATUS_CONFIG[data.status] ?? STATUS_CONFIG.NOT_CONNECTED)
    : STATUS_CONFIG.NOT_CONNECTED;

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard`);
    }).catch(() => {
      message.error('Failed to copy');
    });
  };

  const handleConnect = () => {
    setConnectLoading(true);
    connectPosHub(restaurantId)
      .then((res) => {
        window.location.href = res.authorization_url;
      })
      .catch((err) => {
        message.error(getHttpErrorMessage(err));
        setConnectLoading(false);
      });
  };

  const handleSyncMenu = () => {
    setSyncLoading(true);
    stopMenuSyncPolling();
    syncPosHubMenu(restaurantId)
      .then(() => getPosHubIntegrationStatus(restaurantId))
      .then((fresh) => {
        setData(fresh);
        message.info('Menu sync started. This page will update when it finishes.');
        menuSyncPollCount.current = 0;
        menuSyncPollTimer.current = setInterval(() => {
          menuSyncPollCount.current += 1;
          if (menuSyncPollCount.current > MENU_SYNC_POLL_MAX) {
            stopMenuSyncPolling();
            setSyncLoading(false);
            message.warning('Menu sync is taking longer than expected. Refresh this page in a few minutes.');
            return;
          }
          getPosHubIntegrationStatus(restaurantId)
            .then((d) => {
              setData(d);
              if (!d.menu_sync_in_progress) {
                stopMenuSyncPolling();
                setSyncLoading(false);
                if (d.menu_sync_last_error) {
                  message.error('Menu sync failed.');
                } else {
                  message.success('Menu sync finished.');
                }
              }
            })
            .catch((e) => {
              captureException(e);
            });
        }, MENU_SYNC_POLL_MS);
      })
      .catch((err) => {
        message.error(getHttpErrorMessage(err));
        setSyncLoading(false);
      });
  };

  const handleOpenConsole = () => {
    navigate(`/console/restaurants/${restaurantId}/pos/about`);
  };

  return (
    <StyledWrapper>
      <div className="box">
        <div className="box-title">POS Integration</div>
        <div className="details">
          {loading ? (
            <>
              <SkeletonLoading borderRadius="2px" width="120px" height="24px" count={1} />
              <SkeletonLoading borderRadius="2px" width="200px" height="18px" count={3} />
            </>
          ) : (
            <>
              <div className="details-cell">
                <div className="details-cell-title">Status</div>
                <div className="details-cell-data">
                  <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
                </div>
              </div>

              {!data?.is_connected && (
                <div className="details-cell">
                  {data?.pos_connect_blocked && (
                    <Alert
                      message="POS is disabled for this restaurant"
                      description="POS sync and webhooks are paused. Contact support or an admin to re-enable POS before reconnecting."
                      type="warning"
                      showIcon
                      className="mb-2"
                    />
                  )}
                  <Button
                    type="primary"
                    size="middle"
                    loading={connectLoading}
                    disabled={data?.pos_connect_blocked}
                    onClick={handleConnect}
                    className="pos-action-btn"
                  >
                    {data?.can_reconnect ? 'Reconnect to POS' : 'Connect to POS'}
                  </Button>
                </div>
              )}

              {data?.is_connected && (
                <>
                  <div className="details-cell">
                    <div className="details-cell-title">Account ID</div>
                    <div className="details-cell-data details-cell-data--copyable">
                      <span className="details-cell-data--mono">{data.account_id || '-'}</span>
                      {data.account_id && (
                        <Tooltip title="Copy to clipboard">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopy(data.account_id, 'Account ID')}
                            className="copy-btn"
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <div className="details-cell">
                    <div className="details-cell-title">Location ID</div>
                    <div className="details-cell-data details-cell-data--copyable">
                      <span className="details-cell-data--mono">{data.location_id || '-'}</span>
                      {data.location_id && (
                        <Tooltip title="Copy to clipboard">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopy(data.location_id, 'Location ID')}
                            className="copy-btn"
                          />
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <div className="details-cell">
                    <div className="details-cell-title">Last Menu Sync</div>
                    <div className="details-cell-data">
                      {data.menu_sync_in_progress ? (
                        <Typography.Text type="secondary">Sync in progress…</Typography.Text>
                      ) : data.last_menu_sync ? (
                        <Tooltip title={dayjs(data.last_menu_sync).format('MMM D, YYYY HH:mm')}>
                          <span>{dayjs(data.last_menu_sync).fromNow()}</span>
                        </Tooltip>
                      ) : (
                        'Never'
                      )}
                    </div>
                  </div>

                  {data.menu_sync_last_error && (
                    <Alert
                      message="Last menu sync failed"
                      description={data.menu_sync_last_error}
                      type="error"
                      showIcon
                      className="mt-2"
                    />
                  )}

                  {data.has_errors && (
                    <Alert
                      message="Integration errors detected"
                      description="One or more orders for this restaurant have integration errors."
                      type="warning"
                      showIcon
                      icon={<WarningOutlined />}
                      className="mt-2"
                    />
                  )}

                  <div className="details-cell">
                    <Space>
                      <Button
                        type="primary"
                        size="middle"
                        icon={<ReloadOutlined />}
                        loading={syncLoading}
                        disabled={syncLoading || data?.menu_sync_in_progress}
                        onClick={handleSyncMenu}
                      >
                        Sync Menu
                      </Button>
                      <Button
                        size="middle"
                        onClick={handleOpenConsole}
                      >
                        Manage POS
                      </Button>
                    </Space>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </StyledWrapper>
  );
}

export default PosHubIntegrationBox;
